import { CircleDollarSign, History, PanelLeftClose } from "lucide-react";
import type React from "react";
import { useEffect, useRef } from "react";
import { ChatState, useProject } from "@/features/project";
import { useUI } from "@/features/ui";
import { ChatInputForm } from "./ChatInputForm";
import { ChatMessage } from "./ChatMessage";
import { DataPanel } from "./DataPanel";

const INITIAL_MESSAGE = {
  id: "",
  role: "model",
  content:
    'Hello! I\'m Gabi, your Motion Design Director.\n\n**Tell me what you need:**\n- "Create a high-energy Black Friday promo for sneakers."\n- "Make an elegant intro for a luxury watch brand."\n- "I need a text animation that says \'Coming Soon\' with a glitch effect."\n\n> **Need a different expertise?**\nMeet our [full team of specialized AI Directors](https://ai.gliadirector.com/agents) for unique creative tasks.',
  timestamp: 0,
} as const;

export const ChatPanel: React.FC = () => {
  const { messages, chatState, isLoading, restoreSnapshot, retryMessage, totalCost } = useProject();

  const { showProjects, setShowProjects, setShowChat } = useUI();
  const chatPanelRef = useRef<HTMLDivElement>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  });

  return (
    <div
      ref={chatPanelRef}
      className="relative w-md max-w-full sbs:max-w-md shrink-0 flex flex-col flex-1 min-h-0 border-r border-slate-800 bg-slate-900 overflow-hidden"
    >
      <div className="h-14 flex items-center p-3 border-b border-slate-800 bg-slate-950 shrink-0 justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowProjects(!showProjects)}
            className={`p-1.5 rounded-md transition-colors ${showProjects ? "bg-indigo-600 text-white" : "text-slate-500 hover:text-slate-300 hover:bg-slate-800"}`}
            title="Projects"
          >
            <History size={16} />
          </button>
          <a
            href="https://ai.gliadirector.com/agents"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-sm tracking-tight text-slate-100 hover:text-white"
          >
            GliaDirectors
          </a>
        </div>

        <div className="flex items-center gap-4">
          {totalCost != null && (
            <div
              className="flex items-center gap-1.5 text-[10px] bg-slate-900 py-1 px-2.5 rounded-full border border-slate-800 text-slate-400"
              title="Project Cost"
            >
              <CircleDollarSign size={10} className="text-emerald-500" />
              <span>${totalCost.toFixed(4)}</span>
            </div>
          )}
          <button
            onClick={() => setShowChat(false)}
            className="text-slate-500 hover:text-slate-300"
          >
            <PanelLeftClose size={14} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-hidden relative">
        {isLoading ? (
          <div className="absolute inset-0 overflow-y-auto px-3 pt-1 space-y-2 pb-36">
            {/* Skeleton chat bubbles */}
            <div className="py-3 animate-pulse">
              <div className="h-4 bg-slate-700 rounded w-3/4 mb-2" />
              <div className="h-4 bg-slate-700 rounded w-1/2 mb-2" />
              <div className="h-4 bg-slate-700 rounded w-2/3" />
            </div>
            <div className="py-3 animate-pulse flex justify-end">
              <div className="w-2/3">
                <div className="h-4 bg-slate-700 rounded w-full mb-2" />
                <div className="h-4 bg-slate-700 rounded w-3/4" />
              </div>
            </div>
            <div className="py-3 animate-pulse">
              <div className="h-4 bg-slate-700 rounded w-4/5 mb-2" />
              <div className="h-4 bg-slate-700 rounded w-3/5 mb-2" />
              <div className="h-4 bg-slate-700 rounded w-1/2" />
            </div>
          </div>
        ) : (
          <>
            <div className="absolute inset-0 overflow-y-auto px-3 pt-1 space-y-2 pb-36">
              <ChatMessage key="init" message={INITIAL_MESSAGE} streamingPhase={ChatState.IDLE} />
              {messages.map((msg, idx) => {
                // Hardcode specific guidance link into the first Agent response
                // We find the first message that is from the 'model'
                const isFirstAgentResponse =
                  msg.role === "model" && messages.findIndex((m) => m.role === "model") === idx;

                // Only show the guidance if the message is complete (not the last one being streamed, or if streaming is done)
                const isComplete = idx !== messages.length - 1 || chatState === ChatState.IDLE;

                let displayMessage = msg;
                if (isFirstAgentResponse && msg.content && isComplete) {
                  displayMessage = {
                    ...msg,
                    content:
                      msg.content +
                      "\n\nYou can [download the video](action:highlight-export) anytime.",
                  };
                }

                return (
                  <ChatMessage
                    key={msg.id}
                    message={displayMessage}
                    streamingPhase={idx === messages.length - 1 ? chatState : ChatState.IDLE}
                    onRestore={msg.snapshot ? () => restoreSnapshot(msg.snapshot!) : undefined}
                    onRetry={() => retryMessage(msg.id)}
                    isLast={idx === messages.length - 1}
                  />
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="absolute bottom-0 pl-3 pt-1 pb-0.5 pr-0 left-0 right-4 bg-slate-900 shrink-0">
              {/* Gradient overlay at bottom */}
              <div className="absolute -top-12 left-0 right-0 h-12 bg-linear-to-t from-slate-900 to-transparent pointer-events-none" />
              {/* DataPanel positioned above gradient */}
              <DataPanel chatPanelRef={chatPanelRef} />
              <ChatInputForm />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
